import { Box, Grid, Switch, TextField, Typography } from "@mui/material";
import React from "react";

import useGatAdminConfigData from "../../../../controllers/use-gat-admin-config-data";
import {
	SHIFT_CHANGE_DEFAULT_MAX_PER_MONTH,
	SHIFT_CHANGE_DEFAULT_MIN_NOTICE_HOURS,
} from "../controllers/use-shift-settings-controller";

// GAT-7970. Location values override the company value, which overrides the
// API default. Lives in the location Shift Patterns area and in the company
// Shift Settings (where it sets the company default).

// Upper limits are FE-only — the API stores whatever it is sent and has no
// cap. It also handles bad values badly: anything int.TryParse rejects (over
// 2,147,483,647, decimals) silently falls back to the default (1 change, 0
// hours), and a very large notice makes `StartDateTime.AddHours(-hours)` throw
// on every worker request. A worker has at most one Regular Shift a day, so 31
// changes is already "unlimited"; 720 hours is 30 days' notice.
export const SHIFT_CHANGE_MAX_PER_MONTH_LIMIT = 31;
export const SHIFT_CHANGE_MIN_NOTICE_HOURS_LIMIT = 720;

export const isShiftChangeCountValid = (value: unknown, max: number) =>
	typeof value === "number" &&
	Number.isInteger(value) &&
	value >= 0 &&
	value <= max;

// Both numbers must be whole numbers within range — the API does not validate.
export const isShiftChangeSettingsInvalid = (settings: any) =>
	Boolean(settings?.shiftChangeEnabled) &&
	(!isShiftChangeCountValid(
		settings.shiftChangeMaxPerMonth,
		SHIFT_CHANGE_MAX_PER_MONTH_LIMIT
	) ||
		!isShiftChangeCountValid(
			settings.shiftChangeMinNoticeHours,
			SHIFT_CHANGE_MIN_NOTICE_HOURS_LIMIT
		));

const titleSx = {
	fontFamily: "Roboto",
	fontWeight: 400,
	fontSize: "16px",
	color: "rgba(0, 0, 0, 0.87)",
};

const descriptionSx = {
	fontFamily: "Roboto",
	fontWeight: 400,
	fontSize: "12px",
	color: "rgba(0, 0, 0, 0.6)",
};

const toFieldValue = (value: unknown) =>
	typeof value === "number" && Number.isFinite(value) ? value : "";

// "" while the field is cleared, otherwise the typed number (which may be
// negative or fractional — validation flags those).
const fromFieldValue = (raw: string) => (raw === "" ? "" : Number(raw));

interface ShiftChangeRequestsSettingsProps {
	entity: string;
	newShiftSettings: any;
	setNewShiftSettings: React.Dispatch<React.SetStateAction<any>>;
}

const ShiftChangeRequestsSettings = ({
	entity,
	newShiftSettings,
	setNewShiftSettings,
}: ShiftChangeRequestsSettingsProps) => {
	const { companySettingsObj } = useGatAdminConfigData();

	if (Object.keys(newShiftSettings).length === 0) return null;

	const isLocation = entity === "location";
	const companyMaxPerMonth = Number(
		companySettingsObj?.["shiftChange.maxPerMonth"]?.value ??
			SHIFT_CHANGE_DEFAULT_MAX_PER_MONTH
	);
	const companyMinNotice = Number(
		companySettingsObj?.["shiftChange.minNoticeHours"]?.value ??
			SHIFT_CHANGE_DEFAULT_MIN_NOTICE_HOURS
	);

	const maxPerMonth = newShiftSettings.shiftChangeMaxPerMonth;
	const minNotice = newShiftSettings.shiftChangeMinNoticeHours;
	const isMaxValid = isShiftChangeCountValid(
		maxPerMonth,
		SHIFT_CHANGE_MAX_PER_MONTH_LIMIT
	);
	const isNoticeValid = isShiftChangeCountValid(
		minNotice,
		SHIFT_CHANGE_MIN_NOTICE_HOURS_LIMIT
	);

	const maxHelper = !isMaxValid
		? `Enter a whole number from 0 to ${SHIFT_CHANGE_MAX_PER_MONTH_LIMIT}`
		: maxPerMonth === 0
		? "No new requests allowed"
		: isLocation
		? `Change(s) per month · Company default: ${companyMaxPerMonth}`
		: "Change(s) per month";

	const noticeHelper = !isNoticeValid
		? `Enter a whole number from 0 to ${SHIFT_CHANGE_MIN_NOTICE_HOURS_LIMIT}`
		: minNotice === 0
		? "Hours · until the shift starts"
		: isLocation
		? `Hours · Company default: ${companyMinNotice}`
		: "Hours";

	const handleToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
		const checked = event.target.checked;
		setNewShiftSettings((previous: any) => ({
			...previous,
			shiftChangeEnabled: checked,
		}));
	};

	const handleMaxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const value = fromFieldValue(event.target.value);
		setNewShiftSettings((previous: any) => ({
			...previous,
			shiftChangeMaxPerMonth: value,
		}));
	};

	const handleNoticeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const value = fromFieldValue(event.target.value);
		setNewShiftSettings((previous: any) => ({
			...previous,
			shiftChangeMinNoticeHours: value,
		}));
	};

	const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
		event.target.select();
	};

	const numberFieldSx = {
		"& .MuiInputBase-input": {
			background: "#FFF",
		},
	};

	return (
		<Box
			sx={{
				backgroundColor: "#F5F5F5",
				borderRadius: "8px",
				marginTop: "32px",
				// The Shift Allocation Rules card below has no top margin of its
				// own; it relies on the block above it for the gap.
				marginBottom: "32px",
				padding: "16px",
			}}
		>
			<Box
				sx={{
					display: "flex",
					flexDirection: "row",
					alignItems: "center",
					justifyContent: "space-between",
					gap: "16px",
				}}
			>
				<Box sx={{ display: "flex", flexDirection: "column" }}>
					<Typography sx={titleSx}>Shift change requests</Typography>
					<Typography sx={descriptionSx}>
						Full-time workers on a Shift Pattern can ask to move one scheduled
						Regular Shift to another Shift Schedule on the same day, keeping
						their Job Role and Work Center. Only the worker&apos;s direct manager
						can approve. Requires Full Time Scheduling for the company.
					</Typography>
				</Box>
				<Switch
					checked={Boolean(newShiftSettings.shiftChangeEnabled)}
					onChange={handleToggle}
					inputProps={{ "aria-label": "Shift change requests" }}
				/>
			</Box>
			{newShiftSettings.shiftChangeEnabled ? (
				<Box sx={{ marginTop: "32px" }}>
					<Grid container spacing={2}>
						<Grid item xs={6}>
							<Box sx={{ display: "flex", flexDirection: "column", width: "300px" }}>
								<Typography sx={titleSx}>Changes allowed per month</Typography>
								<Typography sx={{ ...descriptionSx, paddingBottom: "8px" }}>
									Approved changes per worker, counted in the month of the shift
									date. Declined, withdrawn, expired or reverted requests return
									the allowance. 0 turns off new requests.
								</Typography>
								<TextField
									id="shift-change-max-per-month"
									variant="filled"
									type="number"
									sx={numberFieldSx}
									inputProps={{
										min: 0,
										max: SHIFT_CHANGE_MAX_PER_MONTH_LIMIT,
										step: 1,
										"aria-label": "Changes allowed per month",
									}}
									onFocus={handleFocus}
									onChange={handleMaxChange}
									value={toFieldValue(maxPerMonth)}
									error={!isMaxValid}
									helperText={maxHelper}
								/>
							</Box>
						</Grid>
						<Grid item xs={6}>
							<Box sx={{ display: "flex", flexDirection: "column", width: "300px" }}>
								<Typography sx={titleSx}>Minimum notice (hours)</Typography>
								<Typography sx={{ ...descriptionSx, paddingBottom: "8px" }}>
									Hours before the original shift starts. Workers cannot submit
									inside this window, and a pending request expires when it
									passes. 0 means until the shift starts.
								</Typography>
								<TextField
									id="shift-change-min-notice-hours"
									variant="filled"
									type="number"
									sx={numberFieldSx}
									inputProps={{
										min: 0,
										max: SHIFT_CHANGE_MIN_NOTICE_HOURS_LIMIT,
										step: 1,
										"aria-label": "Minimum notice (hours)",
									}}
									onFocus={handleFocus}
									onChange={handleNoticeChange}
									value={toFieldValue(minNotice)}
									error={!isNoticeValid}
									helperText={noticeHelper}
								/>
							</Box>
						</Grid>
					</Grid>
				</Box>
			) : null}
		</Box>
	);
};

export default ShiftChangeRequestsSettings;
